/**
 * 趋势图表组件
 * 支持线图、柱状图、面积图等多种形式，用于展示时序数据和趋势分析
 */

import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { clsx } from 'clsx';
// Using any for now to fix TypeScript compilation
type TimeSeriesDataPoint = any;
type TrendAnalysis = any;

interface TrendChartProps {
  title: string;
  data: TimeSeriesDataPoint[];
  chartType?: 'line' | 'area' | 'bar';
  color?: string;
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
  formatValue?: (value: number) => string;
  formatDate?: (timestamp: string) => string;
  trendAnalysis?: TrendAnalysis;
  thresholds?: {
    warning?: number;
    critical?: number;
  };
  className?: string;
}

const TrendChart: React.FC<TrendChartProps> = ({
  title,
  data,
  chartType = 'line',
  color = '#3b82f6',
  height = 300,
  showGrid = true,
  showTooltip = true,
  formatValue = (value: number) => value.toLocaleString(),
  formatDate = (timestamp: string) => new Date(timestamp).toLocaleDateString('zh-CN'),
  trendAnalysis,
  thresholds,
  className
}) => {
  // 处理数据，添加格式化的日期
  const chartData = data.map(point => ({
    ...point,
    formattedDate: formatDate(point.timestamp),
    timestamp: point.timestamp
  }));

  // 获取趋势颜色
  const getTrendColor = () => {
    if (!trendAnalysis) return color;
    
    switch (trendAnalysis.trend) {
      case 'increasing':
        return '#10b981'; // green
      case 'decreasing':
        return '#ef4444'; // red
      case 'volatile':
        return '#f59e0b'; // amber
      default:
        return color;
    }
  };

  // 获取趋势描述
  const getTrendDescription = () => {
    if (!trendAnalysis) return null;

    const trendLabels = {
      increasing: '上升趋势',
      decreasing: '下降趋势',
      stable: '稳定',
      volatile: '波动较大'
    };

    return (
      <div className="flex items-center space-x-4 text-sm">
        <span className="text-gray-600">
          趋势: <span className={clsx(
            'font-medium',
            trendAnalysis.trend === 'increasing' ? 'text-green-600' :
            trendAnalysis.trend === 'decreasing' ? 'text-red-600' :
            trendAnalysis.trend === 'volatile' ? 'text-amber-600' :
            'text-gray-600'
          )}>
            {trendLabels[trendAnalysis.trend as keyof typeof trendLabels]}
          </span>
        </span>
        
        {Math.abs(trendAnalysis.changeRate) > 0.1 && (
          <span className="text-gray-600">
            变化率: <span className="font-medium">
              {trendAnalysis.changeRate > 0 ? '+' : ''}{trendAnalysis.changeRate.toFixed(1)}%
            </span>
          </span>
        )}
        
        <span className="text-gray-600">
          置信度: <span className="font-medium">
            {(trendAnalysis.confidence * 100).toFixed(0)}%
          </span>
        </span>
      </div>
    );
  };

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-600">
            <span className="font-medium" style={{ color: data.color }}>
              {formatValue(data.value)}
            </span>
          </p>
          {data.payload.metadata && (
            <div className="mt-2 text-xs text-gray-500">
              {Object.entries(data.payload.metadata).map(([key, value]: [string, any]) => (
                <div key={key}>{key}: {value}</div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // 渲染图表
  const renderChart = () => {
    const chartColor = getTrendColor();
    
    const commonProps = {
      width: 800,
      height,
      data: chartData
    };

    const commonAxisProps = {
      tick: { fontSize: 12 },
      axisLine: { stroke: '#e5e7eb' },
      tickLine: { stroke: '#e5e7eb' }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />}
            <XAxis 
              dataKey="formattedDate" 
              {...commonAxisProps}
            />
            <YAxis 
              tickFormatter={formatValue}
              {...commonAxisProps}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {thresholds?.warning && (
              <ReferenceLine y={thresholds.warning} stroke="#f59e0b" strokeDasharray="5 5" />
            )}
            {thresholds?.critical && (
              <ReferenceLine y={thresholds.critical} stroke="#ef4444" strokeDasharray="5 5" />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              fill={chartColor}
              fillOpacity={0.1}
              strokeWidth={2}
            />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />}
            <XAxis 
              dataKey="formattedDate" 
              {...commonAxisProps}
            />
            <YAxis 
              tickFormatter={formatValue}
              {...commonAxisProps}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {thresholds?.warning && (
              <ReferenceLine y={thresholds.warning} stroke="#f59e0b" strokeDasharray="5 5" />
            )}
            {thresholds?.critical && (
              <ReferenceLine y={thresholds.critical} stroke="#ef4444" strokeDasharray="5 5" />
            )}
            <Bar 
              dataKey="value" 
              fill={chartColor}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        );

      default: // line
        return (
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />}
            <XAxis 
              dataKey="formattedDate" 
              {...commonAxisProps}
            />
            <YAxis 
              tickFormatter={formatValue}
              {...commonAxisProps}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {thresholds?.warning && (
              <ReferenceLine y={thresholds.warning} stroke="#f59e0b" strokeDasharray="5 5" />
            )}
            {thresholds?.critical && (
              <ReferenceLine y={thresholds.critical} stroke="#ef4444" strokeDasharray="5 5" />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={2}
              dot={{ fill: chartColor, strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, stroke: chartColor, strokeWidth: 2 }}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className={clsx('card p-6', className)}>
      {/* 标题和趋势分析 */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        {getTrendDescription()}
      </div>

      {/* 图表 */}
      <div className="w-full">
        <ResponsiveContainer>
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* 异常标记 */}
      {trendAnalysis?.anomalies && trendAnalysis.anomalies.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-2">检测到的异常</h4>
          <div className="space-y-1">
            {trendAnalysis.anomalies.slice(0, 3).map((anomaly: any, index: number) => (
              <div key={index} className="flex items-center text-xs text-gray-600">
                <div className={clsx(
                  'w-2 h-2 rounded-full mr-2',
                  anomaly.severity === 'high' ? 'bg-red-500' :
                  anomaly.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                )} />
                <span>
                  {formatDate(anomaly.timestamp)}: {formatValue(anomaly.value)} - {anomaly.reason}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendChart;